import { lazy } from "react";
import * as XLSX from "xlsx";

export const sections = [
  {
    id: "operationAndIndicators",
    title: "Operação e indicadores",
    component: lazy(() => import("./01_OperationAndIndicators")),
  },
  {
    id: "declarations",
    title: "Declarações de compromisso",
    component: lazy(() => import("./02_Declarations")),
  },
  {
    id: "innovationType",
    title: "Tipo de Inovação",
    component: lazy(() => import("./03_InnovationType")),
  },
  {
    id: "projectFramework",
    title: "Enquadramento do Projeto",
    component: lazy(() => import("./04_ProjectFramework")),
  },
  {
    id: "projectFrameworkResearchAndDevelopment",
    title: "Enquadramento do Projeto - I&D",
    component: lazy(
      () => import("./05_ProjectFrameworkResearchAndDevelopment"),
    ),
  },
  {
    id: "projectFrameworkProductiveInnovation",
    title: "Enquadramento do Projeto - Inovação Produtiva",
    component: lazy(() => import("./06_ProjectFrameworkProductiveInnovation")),
  },
  {
    id: "investmentsResearchAndDevelopment",
    title: "Investimentos - I&D",
    component: lazy(() => import("./07_InvestmentsResearchAndDevelopment")),
  },
  {
    id: "investmentsProductiveInnovation",
    title: "Investimentos - Inovação Produtiva",
    component: lazy(() => import("./08_InvestmentsProductiveInnovation")),
  },
  {
    id: "investmentsOtherExpenses",
    title: "Investimentos - Outras Despesas",
    component: lazy(() => import("./09_InvestmentsOtherExpenses")),
  },
  {
    id: "balance",
    title: "Balanço e DR",
    component: lazy(() => import("./10_BalanceDR")),
  },
  {
    id: "indicators",
    title: "Indicadores",
    component: lazy(() => import("./11_Indicators")),
  },
  {
    id: "financingMap",
    title: "Mapa de Financiamento",
    component: lazy(() => import("./12_FinancingMap")),
  },
  {
    id: "selectionCriteria",
    title: "Critérios de seleção",
    component: lazy(() => import("./13_SelectionCriteria")),
  },
];

export const importFiles = {
  Mérito: (workbook, formData, jsonData, handleChange, setToast) => {
    const validationSheet = workbook.Sheets["Condições Validação Inov"];
    const balanceSheet = workbook.Sheets["Balanço"];
    const drSheet = workbook.Sheets["DR"];
    const financingSheet = workbook.Sheets["Financiamento Inov"];
    const investmentsSheet = workbook.Sheets["Quadro de Investimentos Inov"];

    if (
      !validationSheet ||
      !balanceSheet ||
      !drSheet ||
      !financingSheet ||
      !investmentsSheet
    ) {
      setToast({
        title: "Mérito",
        message:
          "Não foi possível carregar o ficheiro: faltam folhas obrigatórias para este aviso.",
        type: "danger",
      });
      return;
    }

    const preProjectYear = toYear(validationSheet["H7"]?.v);
    const postProjectYear = toYear(validationSheet["L7"]?.v);

    const balanceMatrix = getSheetMatrix(balanceSheet);
    const drMatrix = getSheetMatrix(drSheet);
    const financingMatrix = getSheetMatrix(financingSheet);
    const investmentsMatrix = getSheetMatrix(investmentsSheet);

    const balanceYearColumns = {
      preProject: findYearColumn(balanceMatrix, 6, preProjectYear),
      postProject: findYearColumn(balanceMatrix, 6, postProjectYear),
    };

    const drYearColumns = {
      preProject: findYearColumn(drMatrix, 8, preProjectYear),
      postProject: findYearColumn(drMatrix, 8, postProjectYear),
    };

    if (
      balanceYearColumns.preProject < 0 ||
      balanceYearColumns.postProject < 0 ||
      drYearColumns.preProject < 0 ||
      drYearColumns.postProject < 0
    ) {
      setToast({
        title: "Mérito",
        message:
          "Não foi possível localizar as colunas Pré-projeto/Pós-projeto em Balanço e DR.",
        type: "danger",
      });
      return;
    }

    handleChange(
      "balance",
      buildPeriodImport(
        balanceMatrix,
        balanceYearColumns,
        64,
        BALANCE_IMPORT_MAP,
      ),
    );
    handleChange(
      "dr",
      buildPeriodImport(drMatrix, drYearColumns, 22, DR_IMPORT_MAP),
    );
    handleChange(
      "drSupport",
      buildPeriodImport(drMatrix, drYearColumns, 2, DR_SUPPORT_IMPORT_MAP),
    );
    const financingImport = buildFinancingImport(financingMatrix, formData);
    handleChange("financingMap", financingImport.financingMap);
    handleChange("financingSource", financingImport.financingSource);

    const investmentImports = buildInvestmentImports(
      investmentsMatrix,
      formData,
      jsonData,
    );
    handleChange(
      "investmentsResearchAndDevelopment",
      investmentImports.researchAndDevelopment,
    );
    handleChange(
      "investmentsProductiveInnovation",
      investmentImports.productiveInnovation,
    );
    handleChange("investmentsOtherExpenses", investmentImports.otherExpenses);

    setToast({
      title: "Mérito",
      message:
        "Balanço, DR, mapa de financiamento e investimentos foram carregados com sucesso!",
      type: "success",
    });
  },
};

const BALANCE_IMPORT_MAP = [
  {
    targetRow: 2,
    labels: ["Ativos fixos tangíveis"],
    startRow: 10,
    endRow: 20,
  },
  {
    targetRow: 3,
    labels: ["Propriedades de investimento"],
    startRow: 10,
    endRow: 20,
  },
  { targetRow: 4, labels: ["Goodwill"], startRow: 10, endRow: 20 },
  { targetRow: 5, labels: ["Ativos intangíveis"], startRow: 10, endRow: 20 },
  { targetRow: 6, labels: ["Ativos biológicos"], startRow: 10, endRow: 20 },
  {
    targetRow: 7,
    labels: [
      "Participações financeiras - mét eqival patrimonial",
      "Participações financeiras - método da equivalência patrimonial",
    ],
    startRow: 10,
    endRow: 20,
  },
  {
    targetRow: 8,
    labels: ["Participações financeiras - outros métodos"],
    startRow: 10,
    endRow: 20,
  },
  { targetRow: 9, labels: ["Acionistas/sócios"], startRow: 10, endRow: 20 },
  {
    targetRow: 10,
    labels: ["Outros ativos financeiros"],
    startRow: 10,
    endRow: 20,
  },
  {
    targetRow: 11,
    labels: ["Investimentos financeiros"],
    startRow: 10,
    endRow: 20,
  },
  {
    targetRow: 12,
    labels: ["Ativos por impostos diferidos"],
    startRow: 10,
    endRow: 20,
  },
  { targetRow: 14, labels: ["Inventários"], startRow: 22, endRow: 34 },
  { targetRow: 15, labels: ["Ativos biológicos"], startRow: 22, endRow: 34 },
  { targetRow: 16, labels: ["Clientes"], startRow: 22, endRow: 34 },
  {
    targetRow: 17,
    labels: ["Adiantamentos a fornecedores"],
    startRow: 22,
    endRow: 34,
  },
  {
    targetRow: 18,
    labels: [
      "Estados e outros entes públicos",
      "Estado e outros entes públicos",
    ],
    startRow: 22,
    endRow: 34,
  },
  { targetRow: 19, labels: ["Acionistas/sócios"], startRow: 22, endRow: 34 },
  {
    targetRow: 20,
    labels: ["Outras contas a receber"],
    startRow: 22,
    endRow: 34,
  },
  { targetRow: 21, labels: ["Diferimentos"], startRow: 22, endRow: 34 },
  {
    targetRow: 22,
    labels: ["Ativos financeiros detidos para negociação"],
    startRow: 22,
    endRow: 34,
  },
  {
    targetRow: 23,
    labels: ["Outros ativos financeiros"],
    startRow: 22,
    endRow: 34,
  },
  {
    targetRow: 24,
    labels: ["Ativos não correntes detidos para venda"],
    startRow: 22,
    endRow: 34,
  },
  {
    targetRow: 25,
    labels: ["Outros ativos correntes"],
    startRow: 22,
    endRow: 34,
  },
  {
    targetRow: 26,
    labels: ["Caixa e depósitos bancários"],
    startRow: 22,
    endRow: 34,
  },
  { targetRow: 29, labels: ["Capital realizado"], startRow: 37, endRow: 49 },
  {
    targetRow: 30,
    labels: ["Acções quotas próprias", "Ações quotas próprias"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 31,
    labels: ["Outros instrumentos de capital próprio"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 32,
    labels: ["Prémios de emissão", "Premios de emissao"],
    startRow: 37,
    endRow: 49,
  },
  { targetRow: 33, labels: ["Reservas legais"], startRow: 37, endRow: 49 },
  { targetRow: 34, labels: ["Outras reservas"], startRow: 37, endRow: 49 },
  {
    targetRow: 35,
    labels: ["Resultados transitados"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 36,
    labels: ["Ajustamentos em ativos financeiros"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 37,
    labels: ["Excedentes de revalorização"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 38,
    labels: [
      "Outras variações no capital próprio",
      "Outras variacoes no capital proprio",
    ],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 39,
    labels: ["Resultado líquido do período", "Resultado liquido do periodo"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 40,
    labels: ["Interesses minoritários", "Interesses minoritarios"],
    startRow: 37,
    endRow: 49,
  },
  {
    targetRow: 43,
    labels: ["Provisões", "Provisoes"],
    startRow: 53,
    endRow: 57,
  },
  {
    targetRow: 44,
    labels: ["Financiamentos obtidos"],
    startRow: 53,
    endRow: 57,
  },
  {
    targetRow: 45,
    labels: [
      "Responsabilidades por benefícios pós-emprego",
      "Responsabilidades por beneficios pos emprego",
    ],
    startRow: 53,
    endRow: 57,
  },
  {
    targetRow: 46,
    labels: [
      "Passivo por impostos diferidos",
      "Passivos por impostos diferidos",
    ],
    startRow: 53,
    endRow: 57,
  },
  {
    targetRow: 47,
    labels: ["Outras contas a pagar"],
    startRow: 53,
    endRow: 57,
  },
  { targetRow: 49, labels: ["Fornecedores"], startRow: 59, endRow: 69 },
  {
    targetRow: 50,
    labels: ["Adiantamentos de clientes"],
    startRow: 59,
    endRow: 69,
  },
  {
    targetRow: 51,
    labels: [
      "Estado e outros entes públicos",
      "Estados e outros entes públicos",
    ],
    startRow: 59,
    endRow: 69,
  },
  { targetRow: 52, labels: ["Acionistas/sócios"], startRow: 59, endRow: 69 },
  {
    targetRow: 53,
    labels: ["Financiamentos obtidos"],
    startRow: 59,
    endRow: 69,
  },
  {
    targetRow: 54,
    labels: ["Outras contas a pagar"],
    startRow: 59,
    endRow: 69,
  },
  { targetRow: 55, labels: ["Diferimentos"], startRow: 59, endRow: 69 },
  {
    targetRow: 56,
    labels: ["Passivos financeiros detidos para negociação"],
    startRow: 59,
    endRow: 69,
  },
  {
    targetRow: 57,
    labels: ["Outros passivos financeiros"],
    startRow: 59,
    endRow: 69,
  },
  {
    targetRow: 58,
    labels: ["Passivos não correntes detidos para venda"],
    startRow: 59,
    endRow: 69,
  },
  {
    targetRow: 59,
    labels: ["Outros passivos correntes"],
    startRow: 59,
    endRow: 69,
  },
];

const DR_IMPORT_MAP = [
  {
    targetRow: 0,
    labels: ["Vendas e serviços prestados"],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 1,
    labels: ["Subsídios à exploração", "Subsidios a exploracao"],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 2,
    labels: [
      "Ganhos/perdas imputados de subsidiárias, associadas a empreendimentos conjuntos",
      "Ganhos perdas imputados de subsidiarias associadas a empreendimentos conjuntos",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 3,
    labels: [
      "Variação nos inventários da produção",
      "Variacao nos inventarios da producao",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 4,
    labels: [
      "Trabalhos para a própria entidade",
      "Trabalhos para a propria entidade",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 5,
    labels: [
      "CMVMC Custos das mercadorias vendidas e das matérias consumidas",
      "Custos das mercadorias vendidas e das matérias consumidas",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 6,
    labels: [
      "Fornecimentos e serviços externos",
      "Fornecimentos e servicos externos",
    ],
    startRow: 10,
    endRow: 38,
  },
  { targetRow: 7, labels: ["Gastos com o pessoal"], startRow: 10, endRow: 38 },
  {
    targetRow: 8,
    labels: [
      "Imparidade de inventários perdas reversões",
      "Imparidade de inventarios perdas reversoes",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 9,
    labels: [
      "Imparidade de dívidas a receber perdas reversões",
      "Imparidade de dividas a receber perdas reversoes",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 10,
    labels: [
      "Aumentos reduções de justo valor",
      "Aumentos reducoes de justo valor",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 11,
    labels: ["Outros rendimentos e ganhos"],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 12,
    labels: ["Outros gastos e perdas"],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 14,
    labels: [
      "Gastos reversões de depreciações e de amortização",
      "Gastos reversoes de depreciacoes e de amortizacao",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 15,
    labels: [
      "Imparidade de investimentos depreciáveis amortizáveis perdas reversões",
      "Imparidade de investimentos depreciaveis amortizaveis perdas reversoes",
    ],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 17,
    labels: ["Juros e rendimentos similares obtidos"],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 18,
    labels: ["Juros e gastos similares suportados"],
    startRow: 10,
    endRow: 38,
  },
  {
    targetRow: 20,
    labels: [
      "Imposto sobre o rendimento do período",
      "Imposto sobre o rendimento do periodo",
    ],
    startRow: 10,
    endRow: 38,
  },
];

const DR_SUPPORT_IMPORT_MAP = [
  {
    targetRow: 0,
    labels: ["Rendimentos suplementares"],
    startRow: 25,
    endRow: 29,
  },
  {
    targetRow: 1,
    labels: ["Impostos indiretos", "Impostos indirectos"],
    startRow: 25,
    endRow: 29,
  },
];

const FINANCING_IMPORT_MAP = [
  { targetRow: 1, sourceRow: 4 },
  { targetRow: 2, sourceRow: 5 },
  { targetRow: 4, sourceRow: 7 },
  { targetRow: 6, sourceRow: 9 },
  { targetRow: 7, sourceRow: 10 },
  { targetRow: 10, sourceRow: 12 },
  { targetRow: 11, sourceRow: 13 },
  { targetRow: 12, sourceRow: 14 },
  { targetRow: 13, sourceRow: 15 },
  { targetRow: 14, sourceRow: 16 },
  { targetRow: 16, sourceRow: 18 },
];

const INVESTMENT_YEAR_FIELDS = [2025, 2026, 2027, 2028];
const PRODUCTIVE_INVESTMENT_TYPES = new Set([
  normalizeText(
    "Construção de edifícios, obras de remodelação e outras construções",
  ),
  normalizeText("Ativos corpóreos"),
  normalizeText("Ativos incorpóreos"),
]);

function getSheetMatrix(sheet) {
  return XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    blankrows: true,
    defval: "",
    skipHidden: false,
    raw: true,
  });
}

function normalizeText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function toYear(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNumericCell(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : "";
  }

  if (typeof value !== "string") {
    return "";
  }

  const normalized = value.trim().replace(/[^\d,.-]/g, "");
  if (!normalized) {
    return "";
  }

  let numericText = normalized;
  if (numericText.includes(",") && numericText.includes(".")) {
    numericText =
      numericText.lastIndexOf(",") > numericText.lastIndexOf(".")
        ? numericText.replace(/\./g, "").replace(",", ".")
        : numericText.replace(/,/g, "");
  } else if (numericText.includes(",")) {
    numericText = numericText.replace(/\./g, "").replace(",", ".");
  }

  const parsed = Number(numericText);

  return Number.isFinite(parsed) ? parsed : "";
}

function toStringCell(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }

  return String(value).trim();
}

function findYearColumn(matrix, headerRowIndex, year) {
  const targetYear = Number(year);
  if (!Number.isFinite(targetYear)) {
    return -1;
  }

  const headerRow = matrix[headerRowIndex] ?? [];
  return headerRow.findIndex((cell) => Number(cell) === targetYear);
}

function findRowByLabels(
  matrix,
  { labels, startRow = 0, endRow = matrix.length - 1 },
) {
  const candidates = labels.map(normalizeText).filter(Boolean);
  let partialMatch = -1;

  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex += 1) {
    const cellText = normalizeText(matrix[rowIndex]?.[0]);
    if (!cellText) {
      continue;
    }

    if (candidates.some((candidate) => cellText === candidate)) {
      return rowIndex;
    }

    if (
      partialMatch < 0 &&
      candidates.some(
        (candidate) =>
          cellText.includes(candidate) || candidate.includes(cellText),
      )
    ) {
      partialMatch = rowIndex;
    }
  }

  return partialMatch;
}

function readPeriodValue(matrix, rowIndex, columnIndex) {
  if (rowIndex < 0 || columnIndex < 0) {
    return "";
  }

  return toNumericCell(matrix[rowIndex]?.[columnIndex]);
}

function buildPeriodImport(matrix, yearColumns, length, mapping) {
  const rows = Array.from({ length }, () => ({}));

  mapping.forEach(({ targetRow, labels, startRow, endRow }) => {
    const sourceRow = findRowByLabels(matrix, {
      labels,
      startRow,
      endRow,
    });

    const preProject = readPeriodValue(
      matrix,
      sourceRow,
      yearColumns.preProject,
    );
    const postProject = readPeriodValue(
      matrix,
      sourceRow,
      yearColumns.postProject,
    );

    if (preProject !== "" || postProject !== "") {
      rows[targetRow] = { preProject, postProject };
    }
  });

  return rows;
}

function buildFinancingImport(matrix, formData) {
  const financingMap = Array.from({ length: 19 }, () => ({}));
  const yearColumns = {
    2025: findYearColumn(matrix, 2, 2025),
    2026: findYearColumn(matrix, 2, 2026),
    2027: findYearColumn(matrix, 2, 2027),
    2028: findYearColumn(matrix, 2, 2028),
  };

  FINANCING_IMPORT_MAP.forEach(({ targetRow, sourceRow }) => {
    const nextRow = {};

    [2025, 2026, 2027, 2028].forEach((year) => {
      const value = readPeriodValue(matrix, sourceRow, yearColumns[year]);
      if (value !== "") {
        nextRow[`year${year}`] = value;
      }
    });

    if (Object.keys(nextRow).length > 0) {
      financingMap[targetRow] = nextRow;
    }
  });

  return {
    financingMap,
    financingSource: buildFinancingSourceImport(matrix, formData),
  };
}

function buildFinancingSourceImport(matrix, formData) {
  const headerRowIndex = findFinancingSourceHeaderRow(matrix);
  if (headerRowIndex < 0) {
    return [];
  }

  const headerRow = matrix[headerRowIndex] ?? [];
  const priorityColumn = findHeaderColumn(headerRow, ["Prioridade"]);
  const sourceColumn = findHeaderColumn(headerRow, ["Fonte"]);
  const obsColumn = findHeaderColumn(headerRow, [
    "Observações",
    "Observacoes",
    "Observação",
    "Observacao",
    "Obs",
  ]);

  if (sourceColumn < 0 && obsColumn < 0) {
    return [];
  }

  const groupedRows = new Map();
  const existingObsByKey = buildExistingFinancingSourceObsLookup(
    formData?.financingSource,
  );
  let emptyRows = 0;

  for (
    let rowIndex = headerRowIndex + 1;
    rowIndex < matrix.length && rowIndex <= headerRowIndex + 20;
    rowIndex += 1
  ) {
    const priority = toStringCell(matrix[rowIndex]?.[priorityColumn]);
    const source = toStringCell(matrix[rowIndex]?.[sourceColumn]);
    const importedObs = toStringCell(matrix[rowIndex]?.[obsColumn]);

    if (!priority && !source && !importedObs) {
      emptyRows += 1;
      if (groupedRows.size > 0 && emptyRows >= 2) {
        break;
      }
      continue;
    }

    emptyRows = 0;

    const key = buildFinancingSourceKey(priority, source);
    const obs = existingObsByKey.has(key)
      ? existingObsByKey.get(key)
      : importedObs;

    upsertFinancingSourceRow(groupedRows, key, { priority, source, obs });
  }

  return Array.from(groupedRows.values());
}

function findFinancingSourceHeaderRow(matrix) {
  for (let rowIndex = 0; rowIndex < matrix.length; rowIndex += 1) {
    const row = matrix[rowIndex] ?? [];
    const normalizedCells = row.map(normalizeText);
    const hasPriority = normalizedCells.includes(normalizeText("Prioridade"));
    const hasSource = normalizedCells.includes(normalizeText("Fonte"));
    const hasObs = normalizedCells.some((cell) =>
      [
        "Observações",
        "Observacoes",
        "Observação",
        "Observacao",
        "Obs",
      ]
        .map(normalizeText)
        .includes(cell),
    );

    if ((hasPriority && hasSource) || (hasSource && hasObs)) {
      return rowIndex;
    }
  }

  return -1;
}

function findHeaderColumn(headerRow, labels) {
  const normalizedLabels = labels.map(normalizeText);
  return headerRow.findIndex((cell) =>
    normalizedLabels.includes(normalizeText(cell)),
  );
}

function buildInvestmentImports(matrix, formData, jsonData) {
  const rdMap = new Map();
  const productiveMap = new Map();
  const otherExpensesMap = new Map();
  const existingInvestmentObs = buildExistingInvestmentObsLookup(
    formData,
    jsonData,
  );

  matrix.slice(4).forEach((row) => {
    const designation = String(row?.[1] ?? "").trim();
    const category = String(row?.[2] ?? "").trim();
    const rawLocation = String(row?.[3] ?? "").trim();
    const year = toYear(row?.[6]);
    const value = toNumericCell(row?.[7]);

    if (
      !designation ||
      !category ||
      !Number.isFinite(year) ||
      !INVESTMENT_YEAR_FIELDS.includes(year) ||
      value === ""
    ) {
      return;
    }

    const normalizedCategory = normalizeText(category);
    const location = resolveInvestmentLocationId(formData, rawLocation);
    const key = buildInvestmentGroupingKey(designation, category, rawLocation);
    const yearField = `year${year}`;

    if (isOtherExpenseCategory(normalizedCategory)) {
      const typeLabel =
        normalizedCategory === normalizeText("CC/ROC")
          ? "Outras despesas ROC /CC"
          : "Outras despesas";
      const type = findOptionValueByLabel(
        jsonData?.["InvestmentsOtherExpensesType"] ?? [],
        typeLabel,
      );
      const obs = existingInvestmentObs.otherExpenses.has(key)
        ? existingInvestmentObs.otherExpenses.get(key)
        : "";

      upsertInvestmentRow(
        otherExpensesMap,
        key,
        {
          type,
          location,
          designation,
          obs,
        },
        yearField,
        value,
      );
      return;
    }

    if (isResearchAndDevelopmentCategory(normalizedCategory)) {
      const type = findOptionValueByLabel(
        jsonData?.["InvestmentRDType"] ?? [],
        designation,
      );
      const researchLabel =
        normalizedCategory === normalizeText("Investigação Industrial")
          ? "Industrial"
          : "Desenvolvimento Experimental";
      const researchType = findOptionValueByLabel(
        (jsonData?.["InvestmentRDResearchType"] ?? {})[type] ?? [],
        researchLabel,
      );
      const obs = existingInvestmentObs.researchAndDevelopment.has(key)
        ? existingInvestmentObs.researchAndDevelopment.get(key)
        : "";

      upsertInvestmentRow(
        rdMap,
        key,
        {
          type,
          researchType,
          location,
          designation,
          obs,
        },
        yearField,
        value,
      );
      return;
    }

    if (PRODUCTIVE_INVESTMENT_TYPES.has(normalizedCategory)) {
      const type = findOptionValueByLabel(
        jsonData?.["InvestmentProductiveInnovationType"] ?? [],
        category,
      );
      const obs = existingInvestmentObs.productiveInnovation.has(key)
        ? existingInvestmentObs.productiveInnovation.get(key)
        : "";

      upsertInvestmentRow(
        productiveMap,
        key,
        {
          type,
          location,
          designation,
          obs,
        },
        yearField,
        value,
      );
    }
  });

  return {
    researchAndDevelopment: finalizeInvestmentRows(rdMap),
    productiveInnovation: finalizeInvestmentRows(productiveMap),
    otherExpenses: finalizeInvestmentRows(otherExpensesMap),
  };
}

function isOtherExpenseCategory(normalizedCategory) {
  return (
    normalizedCategory.includes(normalizeText("CC/ROC")) ||
    normalizedCategory.includes(normalizeText("Outras despesas"))
  );
}

function isResearchAndDevelopmentCategory(normalizedCategory) {
  return (
    normalizedCategory === normalizeText("Investigação Industrial") ||
    normalizedCategory === normalizeText("Desenvolvimento Experimental")
  );
}

function resolveInvestmentLocationId(formData, rawLocation) {
  const locationLabel = normalizeInvestmentLocationLabel(rawLocation);

  if (!locationLabel) {
    return "";
  }

  const location = (formData?.projectLocation ?? []).find((item) => {
    const description = normalizeText(item?.description);
    const address = normalizeText(item?.address);
    const target = normalizeText(locationLabel);
    return description === target || address === target;
  });

  return location?.id ? String(location.id) : "";
}

function findOptionValueByLabel(options, label) {
  const target = normalizeText(label);
  const match = options.find(
    (option) => normalizeText(option?.label) === target,
  );
  return match?.value ?? "";
}

function findOptionLabelByValue(options, value) {
  const target = String(value ?? "");
  const match = options.find((option) => String(option?.value) === target);
  return match?.label ?? "";
}

function normalizeInvestmentLocationLabel(value) {
  return String(value ?? "")
    .replace(/^\s*\d+\s*-\s*/, "")
    .trim();
}

function buildInvestmentGroupingKey(designation, category, rawLocation) {
  return [
    normalizeText(designation),
    normalizeText(category),
    normalizeText(normalizeInvestmentLocationLabel(rawLocation)),
  ].join("||");
}

function buildFinancingSourceKey(priority, source) {
  return [normalizeText(priority), normalizeText(source)].join("||");
}

function buildExistingFinancingSourceObsLookup(rows) {
  const lookup = new Map();

  (rows ?? []).forEach((row) => {
    const key = buildFinancingSourceKey(row?.priority, row?.source);
    if (!key.replace(/\|/g, "")) {
      return;
    }

    if (!lookup.has(key)) {
      lookup.set(key, row?.obs ?? "");
    }
  });

  return lookup;
}

function buildExistingInvestmentObsLookup(formData, jsonData) {
  const locationLabelsById = buildInvestmentLocationLabelsById(
    formData?.projectLocation,
  );

  return {
    researchAndDevelopment: buildExistingInvestmentObsMap(
      formData?.investmentsResearchAndDevelopment,
      (row) => {
        const researchTypeLabel = findOptionLabelByValue(
          (jsonData?.["InvestmentRDResearchType"] ?? {})[row?.type] ?? [],
          row?.researchType,
        );
        const category =
          normalizeText(researchTypeLabel) === normalizeText("Industrial")
            ? "Investigação Industrial"
            : researchTypeLabel;

        return buildInvestmentGroupingKeys(
          row?.designation,
          category,
          resolveStoredInvestmentLocationLabels(locationLabelsById, row),
        );
      },
    ),
    productiveInnovation: buildExistingInvestmentObsMap(
      formData?.investmentsProductiveInnovation,
      (row) =>
        buildInvestmentGroupingKeys(
          row?.designation,
          findOptionLabelByValue(
            jsonData?.["InvestmentProductiveInnovationType"] ?? [],
            row?.type,
          ),
          resolveStoredInvestmentLocationLabels(locationLabelsById, row),
        ),
    ),
    otherExpenses: buildExistingInvestmentObsMap(
      formData?.investmentsOtherExpenses,
      (row) => {
        const typeLabel = findOptionLabelByValue(
          jsonData?.["InvestmentsOtherExpensesType"] ?? [],
          row?.type,
        );
        const category = normalizeText(typeLabel).includes(normalizeText("ROC /CC"))
          ? "CC/ROC"
          : "Outras despesas";

        return buildInvestmentGroupingKeys(
          row?.designation,
          category,
          resolveStoredInvestmentLocationLabels(locationLabelsById, row),
        );
      },
    ),
  };
}

function buildExistingInvestmentObsMap(rows, getKey) {
  const lookup = new Map();

  (rows ?? []).forEach((row) => {
    const keys = getKey(row).filter((key) => key.replace(/\|/g, ""));
    if (!keys.length) {
      return;
    }

    keys.forEach((key) => {
      if (!lookup.has(key)) {
        lookup.set(key, row?.obs ?? "");
      }
    });
  });

  return lookup;
}

function buildInvestmentLocationLabelsById(locations) {
  const lookup = new Map();

  (locations ?? []).forEach((location) => {
    const key = String(location?.id ?? "");
    if (!key) {
      return;
    }

    lookup.set(
      key,
      [location?.description, location?.address].filter(Boolean),
    );
  });

  return lookup;
}

function buildInvestmentGroupingKeys(designation, category, rawLocations) {
  const locations = Array.isArray(rawLocations) ? rawLocations : [rawLocations];
  const uniqueLocations = Array.from(
    new Set(locations.map(normalizeInvestmentLocationLabel).filter(Boolean)),
  );

  if (!uniqueLocations.length) {
    return [buildInvestmentGroupingKey(designation, category, "")];
  }

  return uniqueLocations.map((rawLocation) =>
    buildInvestmentGroupingKey(designation, category, rawLocation),
  );
}

function resolveStoredInvestmentLocationLabels(locationLabelsById, row) {
  const storedLocation = String(row?.location ?? row?.institute ?? "");
  return (
    locationLabelsById.get(storedLocation) ?? [row?.location ?? row?.institute ?? ""]
  );
}

function upsertInvestmentRow(map, key, baseRow, yearField, value) {
  const existing = map.get(key) ?? {
    ...baseRow,
    year2025: "",
    year2026: "",
    year2027: "",
    year2028: "",
  };

  if (!existing.obs) {
    existing.obs = baseRow.obs ?? "";
  }
  existing[yearField] =
    (toNumericCell(existing[yearField]) || 0) + (toNumericCell(value) || 0);

  map.set(key, existing);
}

function upsertFinancingSourceRow(map, key, baseRow) {
  const existing = map.get(key) ?? { ...baseRow };

  existing.priority = existing.priority || baseRow.priority;
  existing.source = existing.source || baseRow.source;
  if (!existing.obs) {
    existing.obs = baseRow.obs ?? "";
  }

  map.set(key, existing);
}

function finalizeInvestmentRows(map) {
  return Array.from(map.values()).map((row, index) => ({
    id: index + 1,
    ...row,
  }));
}
